import type {
  BasicData,
  FlowRatesInSitu,
  FlowRatePrerequisites,
  FlowRateDependants
} from "./d";
import { PoundsPerCubicFoot } from "@/libs/units/density";
import {
  oilSpecificHeatCapacity,
  waterSpecificHeatCapacity,
  liquidSpecificHeatCapacityCl
} from "../electrical";
import {
  oil_phase_surface_tension,
  water_phase_surface_tension,
  liquids_surface_tension
} from "../oilPhasePVT";
import {
  calculateBubbleRiseRadialVelocity,
  calculateBubbleRiseVerticalVelocity,
  get_liquid_density,
  get_liquid_viscosity,
  get_mix_superficial_velocity,
  getFluidDensityLBMFT3,
  getGasSuperficialVelocityFtSec,
  getLiquidSuperficialVelocityFtSec
} from "../pressureDropCorrelations";
import { getGasFlowVolumeRatio } from "../gasPhasePVT";

export function getFlowRateDependants(
  basicData: BasicData,
  flowRatePrerequisites: FlowRatePrerequisites,
  flowRatesInSitu: FlowRatesInSitu,
  pressurePSIA: number,
  temperatureF: number,
  pipeDiameterIn: number,
  equipmentCrossSectionFt2: number,
  fluidViscosityCPOverride?: number | null
): FlowRateDependants {
  const { oilAPI } = basicData;
  const {
    oilDensityLBMFT3,
    waterDensityLBMFT3,
    gasDensityLBMFT3,
    condensateDensityLBMFT3,
    oilViscosityCP,
    waterViscosityCP,
    gasViscosityCP,
    oilSpecificGravity,
    oilPhaseGasSolubilitySCFSTB,
    waterPhaseGasSolubilitySCFSTB
  } = flowRatePrerequisites;
  const {
    liquidFlowRateBBLD,
    oilFlowRateBBLD,
    waterFlowRateBBLD,
    waterCutRatio,
    condensateRatio,
    gasOilRatioSCFSTB,
    gasLiquidRatioSCFSTB,
    freeGasFlowRateBBLD
  } = flowRatesInSitu;

  // GVF
  const gasFlowVolumeRatio = getGasFlowVolumeRatio(
    freeGasFlowRateBBLD,
    liquidFlowRateBBLD
  );

  // rho_l
  const liquidDensityLBMFT3 = get_liquid_density(
    oilDensityLBMFT3,
    waterDensityLBMFT3,
    waterCutRatio,
    condensateDensityLBMFT3,
    condensateRatio
  );

  const fluidDensityLBMFT3 = getFluidDensityLBMFT3(
    liquidDensityLBMFT3,
    gasDensityLBMFT3,
    gasFlowVolumeRatio
  );

  const liquidSpecificGravity =
    PoundsPerCubicFoot.toStandardUnit(liquidDensityLBMFT3);

  const fluidSpecificGravity =
    PoundsPerCubicFoot.toStandardUnit(fluidDensityLBMFT3);

  // sigma_oil
  const oilSurfaceTensionDYCM = oil_phase_surface_tension(oilAPI, pressurePSIA);

  // sigma_water
  const waterSurfaceTensionDYCM = water_phase_surface_tension(
    pressurePSIA,
    temperatureF
  );

  // sigma_liquids
  const liquidSurfaceTensionDYCM = liquids_surface_tension(
    oilSurfaceTensionDYCM,
    waterSurfaceTensionDYCM,
    waterCutRatio
  );

  // mu_l
  const liquidViscosityCP = get_liquid_viscosity(
    oilViscosityCP,
    waterViscosityCP,
    waterCutRatio
  );

  const calculatedFluidViscosityCP =
    (gasViscosityCP * freeGasFlowRateBBLD +
      liquidViscosityCP * liquidFlowRateBBLD) /
      (freeGasFlowRateBBLD + liquidFlowRateBBLD) || 0;

  const fluidViscosityCP =
    fluidViscosityCPOverride ?? calculatedFluidViscosityCP;

  // C_o
  const oilSpecificHeat = oilSpecificHeatCapacity(
    temperatureF,
    oilSpecificGravity
  );

  // C_w
  const waterSpecificHeat = waterSpecificHeatCapacity(temperatureF);

  // C_l
  const liquidSpecificHeatCapacity = liquidSpecificHeatCapacityCl(
    oilSpecificHeat,
    waterSpecificHeat,
    oilFlowRateBBLD,
    waterFlowRateBBLD,
    liquidFlowRateBBLD,
    oilDensityLBMFT3,
    waterDensityLBMFT3,
    liquidDensityLBMFT3
  );

  // V_sl
  const liquidSuperficialVelocityFTSEC = getLiquidSuperficialVelocityFtSec(
    liquidFlowRateBBLD,
    pipeDiameterIn,
    equipmentCrossSectionFt2
  );

  // V_sg
  const gasSuperficialVelocityFTSEC = getGasSuperficialVelocityFtSec(
    freeGasFlowRateBBLD,
    oilFlowRateBBLD,
    pipeDiameterIn,
    gasOilRatioSCFSTB,
    gasLiquidRatioSCFSTB,
    oilPhaseGasSolubilitySCFSTB,
    waterPhaseGasSolubilitySCFSTB,
    equipmentCrossSectionFt2
  );

  // V_m
  const mixSuperficialVelocityFTSEC = get_mix_superficial_velocity(
    liquidSuperficialVelocityFTSEC,
    gasSuperficialVelocityFTSEC
  );

  const bubbleRiseVerticalVelocityFTSEC = calculateBubbleRiseVerticalVelocity(
    liquidDensityLBMFT3,
    gasDensityLBMFT3
  );

  const bubbleRiseRadialVelocityFTSEC = calculateBubbleRiseRadialVelocity(
    liquidDensityLBMFT3,
    gasDensityLBMFT3,
    liquidSurfaceTensionDYCM
  );

  return {
    equipmentCrossSectionFt2,
    gasFlowVolumeRatio,
    liquidDensityLBMFT3,
    fluidDensityLBMFT3,
    liquidSpecificGravity,
    fluidSpecificGravity,
    // sigma Surface Tension
    oilSurfaceTensionDYCM,
    waterSurfaceTensionDYCM,
    liquidSurfaceTensionDYCM,
    liquidViscosityCP,
    fluidViscosityCP,
    // C Specific Heat Capacity
    oilSpecificHeat,
    waterSpecificHeat,
    liquidSpecificHeatCapacity,
    // superficials
    liquidSuperficialVelocityFTSEC,
    gasSuperficialVelocityFTSEC,
    mixSuperficialVelocityFTSEC,
    bubbleRiseVerticalVelocityFTSEC,
    bubbleRiseRadialVelocityFTSEC
  };
}
